'use client';
import React, { useState, useEffect, useRef } from 'react';
import Link from 'next/link';

interface Job {
  title: string;
  brand: string;
  category: string;
  budget: string;
  duration: string;
  location: string;
  tags: string[];
  urgent: boolean;
  posted: string;
  accent: string;
  logo: number;
}

const jobs: Job[] = [
  {
    title: 'TikTok Live Seller — Skincare & Beauty',
    brand: 'GlowCo Beauty',
    category: 'Beauty',
    budget: '$5,000–$12,000/mo',
    duration: '3 months',
    location: 'Remote',
    tags: ['Skincare', 'Beauty', 'English'],
    urgent: true,
    posted: '2h ago',
    accent: 'bg-theme-01',
    logo: 10,
  },
  {
    title: 'Live Commerce Host — Fashion & Apparel',
    brand: 'UrbanThread Co.',
    category: 'Fashion',
    budget: '$8,000–$20,000/mo',
    duration: '6 months',
    location: 'Remote',
    tags: ['Fashion', 'Streetwear', 'Bilingual'],
    urgent: false,
    posted: '5h ago',
    accent: 'bg-theme-04',
    logo: 12,
  },
  {
    title: 'TikTok Shop Streamer — Health Supplements',
    brand: 'VitaBoost Labs',
    category: 'Health',
    budget: '$4,000–$9,000/mo',
    duration: '1 month',
    location: 'Remote',
    tags: ['Health', 'Wellness', 'Fitness'],
    urgent: true,
    posted: '1d ago',
    accent: 'bg-theme-03',
    logo: 14,
  },
  {
    title: 'Live Seller — Consumer Electronics',
    brand: 'TechNova',
    category: 'Tech',
    budget: '$10,000–$25,000/mo',
    duration: 'Ongoing',
    location: 'Remote',
    tags: ['Tech', 'Gadgets', 'Gaming'],
    urgent: false,
    posted: '2d ago',
    accent: 'bg-theme-02',
    logo: 16,
  },
  {
    title: 'TikTok Live Host — Home & Kitchen',
    brand: 'NestWell',
    category: 'Home',
    budget: '$3,500–$7,000/mo',
    duration: '2 months',
    location: 'Remote',
    tags: ['Home', 'Kitchen', 'Lifestyle'],
    urgent: false,
    posted: '3d ago',
    accent: 'bg-theme-04',
    logo: 18,
  },
  {
    title: 'Live Commerce Presenter — Food & Snacks',
    brand: 'SnackHaus',
    category: 'Food',
    budget: '$6,000–$14,000/mo',
    duration: '4 months',
    location: 'Remote',
    tags: ['Food', 'Snacks', 'Asian Cuisine'],
    urgent: true,
    posted: '4d ago',
    accent: 'bg-theme-01',
    logo: 20,
  },
];

const categories = ['All', 'Beauty', 'Fashion', 'Health', 'Tech', 'Home', 'Food'];

export default function JobListingsSection() {
  const [activeCategory, setActiveCategory] = useState('All');
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const cards = entry.target.querySelectorAll('.job-card');
            cards.forEach((card, i) => {
              setTimeout(() => {
                (card as HTMLElement).style.opacity = '1';
                (card as HTMLElement).style.transform = 'translateY(0)';
              }, i * 100);
            });
          }
        });
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const filteredJobs = activeCategory === 'All'
    ? jobs
    : jobs.filter(j => j.category === activeCategory);

  return (
    <section id="job-board" className="py-24 bg-white" ref={sectionRef}>
      <div className="max-w-[1200px] mx-auto px-6">
        {/* Title */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
          <div>
            <div className="mb-5">
              <div
                className="bg-theme-03 text-dark text-[14px] font-semibold px-4 py-1.5 rounded-2xl inline-block"
                style={{ transform: 'rotate(10deg)' }}
              >
                Job Board
              </div>
            </div>
            <h2 className="text-[40px] md:text-[48px] font-bold text-dark leading-tight mb-3">
              Live stream jobs, open now
            </h2>
            <p className="text-[17px] text-dark/60 max-w-[500px] leading-relaxed">
              Brands are actively hiring TikTok Shop live sellers. Apply in minutes.
            </p>
          </div>
          <a
            href="#"
            className="flex-shrink-0 font-bold text-[15px] text-white bg-dark px-7 py-3.5 rounded-pill hover:bg-opacity-80 transition-all duration-200"
          >
            Post a Job
          </a>
        </div>

        {/* Category filter */}
        <div className="flex flex-wrap gap-2 mb-8">
          {categories.map((cat) => (
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              className={`font-semibold text-[14px] px-5 py-2 rounded-full transition-all duration-200 ${
                activeCategory === cat
                  ? 'bg-dark text-white' :'bg-neutral-01 text-dark hover:bg-neutral-02'
              }`}
            >
              {cat}
            </button>
          ))}
        </div>

        {/* Job cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {filteredJobs.map((job, i) => (
            <div
              key={i}
              className="job-card bg-neutral-01 rounded-[24px] p-6 hover:-translate-y-1 hover:shadow-lg transition-all duration-300 cursor-pointer group"
              style={{ opacity: 0, transform: 'translateY(30px)', transition: 'opacity 0.5s ease, transform 0.5s ease' }}
            >
              {/* Header */}
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className={`w-11 h-11 ${job.accent} rounded-2xl flex items-center justify-center flex-shrink-0`}>
                    <img
                      src={`https://i.pravatar.cc/44?img=${job.logo}`}
                      alt={job.brand}
                      className="w-8 h-8 rounded-xl object-cover"
                    />
                  </div>
                  <div>
                    <p className="font-bold text-dark text-[15px] leading-tight">{job.title}</p>
                    <p className="text-dark/50 text-[13px] mt-0.5">{job.brand}</p>
                  </div>
                </div>
                {job.urgent && (
                  <span className="flex-shrink-0 text-[11px] font-bold text-white bg-dark px-3 py-1 rounded-full">
                    Urgent
                  </span>
                )}
              </div>

              {/* Details */}
              <div className="flex flex-wrap gap-3 mb-4">
                <div className="flex items-center gap-1.5 text-[13px] text-dark/60">
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
                    <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" fill="currentColor"/>
                  </svg>
                  {job.location}
                </div>
                <div className="flex items-center gap-1.5 text-[13px] text-dark/60">
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
                    <path d="M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67V7z" fill="currentColor"/>
                  </svg>
                  {job.duration}
                </div>
                <div className="flex items-center gap-1.5 text-[13px] font-semibold text-dark">
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
                    <path d="M11.8 10.9c-2.27-.59-3-1.2-3-2.15 0-1.09 1.01-1.85 2.7-1.85 1.78 0 2.44.85 2.5 2.1h2.21c-.07-1.72-1.12-3.3-3.21-3.81V3h-3v2.16c-1.94.42-3.5 1.68-3.5 3.61 0 2.31 1.91 3.46 4.7 4.13 2.5.6 3 1.48 3 2.41 0 .69-.49 1.79-2.7 1.79-2.06 0-2.87-.92-2.98-2.1h-2.2c.12 2.19 1.76 3.42 3.68 3.83V21h3v-2.15c1.95-.37 3.5-1.5 3.5-3.55 0-2.84-2.43-3.81-4.7-4.4z" fill="currentColor"/>
                  </svg>
                  {job.budget}
                </div>
              </div>

              {/* Tags */}
              <div className="flex flex-wrap gap-1.5 mb-5">
                {job.tags.map((tag) => (
                  <span key={tag} className="text-[11px] font-medium text-dark/60 bg-white px-3 py-1 rounded-full">
                    {tag}
                  </span>
                ))}
              </div>

              {/* Footer */}
              <div className="flex items-center justify-between pt-4 border-t border-neutral-02">
                <span className="text-[12px] text-dark/40 font-medium">Posted {job.posted}</span>
                <Link href="/jobs/job-001" className="font-bold text-[13px] text-dark group-hover:text-theme-01 transition-colors duration-200 flex items-center gap-1">
                  Apply Now
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                    <path d="M5 12h14M12 5l7 7-7 7" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </Link>
              </div>
            </div>
          ))}
        </div>

        {/* Load more */}
        <div className="text-center mt-10">
          <button className="font-bold text-[15px] text-dark border border-dark/20 px-8 py-4 rounded-pill hover:bg-neutral-01 transition-all duration-200">
            Load More Jobs
          </button>
        </div>
      </div>
    </section>
  );
}
